import { ReactNode, useMemo, useRef } from "react";
import { QueryClient, useQueryClient } from "@tanstack/react-query";

import Button from "components/ui/button/Button";
import { toast } from "components/ui/toast/Toast";
import { freshToken } from "lib/hooks/api/useAppToken";
import { useProvidedAppToken } from "lib/providers/ApiTokenProvider";
import { settingsRoutes } from "lib/routes";
import { Configuration, DefaultApi, Middleware } from "./generated/integration-api";
import { defineInjectable } from "./reactInjector";

// Backend code returned by the integrations service when a stored refresh token is expired/revoked
// and the only recovery is the user reconnecting the integration.
const RECONNECT_REQUIRED_CODE = "INTEGRATION_RECONNECT_REQUIRED";

type ReconnectErrorBody = { code: string; providerType?: string };

function isReconnectRequiredBody(body: unknown): body is ReconnectErrorBody {
  return (
    typeof body === "object" &&
    body !== null &&
    (body as { code?: unknown }).code === RECONNECT_REQUIRED_CODE
  );
}

const FRIENDLY_PROVIDER_NAMES: Record<string, string> = {
  HUBSPOT: "HubSpot",
  SALESFORCE: "Salesforce",
};

function friendlyProviderName(providerType?: string): string {
  return (providerType && FRIENDLY_PROVIDER_NAMES[providerType]) || "CRM";
}

/**
 * Every CRM data call routes through this client, so a single post-response middleware lets us
 * surface an expired-connection prompt once, regardless of which call triggered it. The stable
 * toast id de-dupes concurrent failures so the user only sees one prompt.
 */
const reconnectToastMiddleware: Middleware = {
  async post({ response }) {
    if (response.status !== 401) {
      return;
    }
    try {
      const body: unknown = await response.clone().json();
      if (isReconnectRequiredBody(body)) {
        toast.warning(`Your ${friendlyProviderName(body.providerType)} connection has expired`, {
          id: "crm-reconnect-required",
          description: "Reconnect the integration to keep syncing with your CRM.",
          action: (
            <Button
              as="a"
              to={settingsRoutes.integrations}
              buttonType="secondary"
              // Router adds a literal "active" class when `to` matches the current route, which
              // collides with Button's "&.active" pressed styling. Override it so the CTA doesn't
              // render stuck-pressed when the toast fires on the integrations settings page itself.
              activeProps={{}}
            >
              Reconnect
            </Button>
          ),
        });
      }
    } catch {
      // Body wasn't JSON / already consumed — nothing to surface.
    }
  },
};

function createIntegrationApi(getToken: () => string, queryClient?: QueryClient): DefaultApi {
  return new DefaultApi(
    new Configuration({
      // locally we need to define the integration service url but in sandbox and prod it's just base
      basePath: window.integration_service_url,
      accessToken: () => freshToken(queryClient, getToken()),
      middleware: [reconnectToastMiddleware],
    }),
  );
}

const INTEGRATION_API_INJECTABLE = defineInjectable<DefaultApi>("IntegrationApi", () =>
  // Consumers rendered outside the provider get an unauthenticated client rather than a crash,
  // matching how AppTokenContext defaults to an empty token.
  createIntegrationApi(() => ""),
);

export const useIntegrationApi = INTEGRATION_API_INJECTABLE.useInstance;

/**
 * Publishes one integrations-service client, authorised with the app token that React Query keeps
 * fresh. Built once and never rebuilt on a token refresh — it reads the token from a ref per
 * request, so a refresh cannot strand a dead token in anything holding the old client.
 *
 * Must be rendered inside AppTokenProvider, otherwise the token is always empty.
 */
export function IntegrationAPIProvider({
  children,
  instance,
}: {
  children: ReactNode;
  /** Overrides the client, for tests. */
  instance?: DefaultApi;
}): JSX.Element {
  const token = useProvidedAppToken();
  const queryClient = useQueryClient();
  const tokenRef = useRef(token);
  // Not in an effect: child effects run before parents, so a child fetching there would send the
  // stale token. Safe during render — it is only read later, when a request builds its headers.
  tokenRef.current = token;

  const api = useMemo(
    () => createIntegrationApi(() => tokenRef.current, queryClient),
    [queryClient],
  );

  return (
    <INTEGRATION_API_INJECTABLE.Provider instance={instance ?? api}>
      {children}
    </INTEGRATION_API_INJECTABLE.Provider>
  );
}
